/**
 * A picture of what the viewer is showing.
 *
 * The obvious implementation - `canvas.toDataURL()` on a WebGL canvas - needs
 * `preserveDrawingBuffer: true`, which costs memory and a copy on every frame
 * for every user of every viewer, to serve a button most of them will never
 * press. Both viewers already draw **on demand** rather than in a loop, so
 * there is a cheaper and better-defined route: read the pixels back inside the
 * same frame that drew them. The drawing buffer is only discarded when the
 * frame is composited, so a read from within the callback that issued the draw
 * is valid whether or not it is preserved.
 *
 * What comes back is exactly the canvas: the model as framed, and none of the
 * interface around it. If the read fails - a lost context, a browser that
 * refuses - the caller gets `null` and says so, rather than handing somebody a
 * blank PNG and calling it their model.
 */

/** Read the current drawing buffer. Must be called inside the drawing frame. */
export async function capturePng(
  gl: WebGLRenderingContext,
  canvas: HTMLCanvasElement,
): Promise<Blob | null> {
  const width = canvas.width;
  const height = canvas.height;
  if (width < 1 || height < 1) return null;

  let pixels: Uint8Array;
  try {
    pixels = new Uint8Array(width * height * 4);
    gl.readPixels(0, 0, width, height, gl.RGBA, gl.UNSIGNED_BYTE, pixels);
  } catch {
    return null;
  }

  const out = document.createElement("canvas");
  out.width = width;
  out.height = height;
  const context = out.getContext("2d");
  if (!context) return null;

  const image = context.createImageData(width, height);
  // WebGL's origin is bottom-left and a canvas image's is top-left, so the
  // rows go back in reverse. Without this the model arrives upside down.
  for (let row = 0; row < height; row += 1) {
    const from = (height - 1 - row) * width * 4;
    image.data.set(pixels.subarray(from, from + width * 4), row * width * 4);
  }
  context.putImageData(image, 0, 0);

  return new Promise((resolve) => {
    try {
      out.toBlob((blob) => resolve(blob), "image/png");
    } catch {
      resolve(null);
    }
  });
}

/** Hand the picture to the browser, and revoke the URL immediately after. */
export function saveImage(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  anchor.click();
  URL.revokeObjectURL(url);
}
